import Select, { components } from "react-select";
import { Icons } from "../../constants/icon-library";
import { useState } from "react";
import { AVATAR_COLORS } from "../../constants/constants";
import { getInitials } from "../../platforms/Meetings/Meetings.utils";

const CustomValueContainer = ({ children, ...props }) => {
  return (
    <components.ValueContainer {...props}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          minHeight: "28px",
          height: "auto",
          width: "100%",
          minWidth: 0,
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0 8px",
            paddingTop: "0px",
            flexShrink: 0,
          }}
        >
          <i className={`wb ${Icons("down")} fs-10`} />
        </span>
        <span
          style={{
            width: "1px",
            alignSelf: "stretch",
            background: "var(--phoenix-border-color-translucent)",
            marginRight: "8px",
            flexShrink: 0,
          }}
        />
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            minWidth: 0,
            flex: 1,
            gap: "4px",
            padding: "0px",
          }}
        >
          {children}
        </div>
      </div>
    </components.ValueContainer>
  );
};

const getColor = (name = "") =>
  AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

const AvatarValueContainer = ({ children, getValue, selectProps }) => {
  const [hoveredValue, setHoveredValue] = useState(null);
  const selected = getValue();
  const MAX = 5;
  const visible = selected.slice(0, MAX);
  const overflow = selected.length - MAX;

  const handleRemove = (e, optValue) => {
    e.stopPropagation();
    e.preventDefault();
    const next = selected.filter((o) => o.value !== optValue);
    selectProps.onChange(next, { action: "remove-value" });
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        minHeight: "24px",
        paddingLeft: "2px",
        flex: 1,
        overflow: "hidden",
      }}
    >
      {selected.length === 0 ? (
        <span
          style={{
            color: "#aaa",
            fontSize: 12,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            paddingLeft: "6px",
            width: "100%",
            display: "block",
          }}
        >
          {selectProps.placeholder}
        </span>
      ) : (
        <div style={{ display: "flex", alignItems: "center" }}>
          {visible.map((opt) => {
            const isHovered = hoveredValue === opt.value;
            return (
              <span
                key={opt.value}
                title={opt.label}
                onMouseEnter={() => setHoveredValue(opt.value)}
                onMouseLeave={() => setHoveredValue(null)}
                onMouseDown={
                  isHovered ? (e) => handleRemove(e, opt.value) : undefined
                }
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: isHovered ? "#e06c75" : getColor(opt.label),
                  color: "#fff",
                  fontSize: isHovered ? 14 : 10,
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginLeft: "-4px",
                  flexShrink: 0,
                  cursor: isHovered ? "pointer" : "default",
                  transition: "background 0.15s, font-size 0.1s",
                  userSelect: "none",
                }}
              >
                {isHovered ? "×" : getInitials(opt.label)}
              </span>
            );
          })}

          {overflow > 0 && (
            <span
              title={selected
                .slice(MAX)
                .map((o) => o.label)
                .join(", ")}
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                background: "#8a94ad",
                color: "#fff",
                fontSize: 10,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid #fff",
                marginLeft: "-4px",
                flexShrink: 0,
              }}
            >
              +{overflow}
            </span>
          )}
        </div>
      )}

      <div
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          opacity: 0,
          pointerEvents: "none",
        }}
      >
        {children}
      </div>
    </div>
  );
};

const CustomOption = (props) => {
  const { data, innerRef, innerProps, isFocused, isSelected } = props;

  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        padding: "4px 8px",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        backgroundColor: isSelected
          ? "var(--phoenix-primary)"
          : isFocused
            ? "var(--phoenix-primary)"
            : "transparent",
        color:
          isSelected || isFocused ? "var(--phoenix-emphasis-bg)" : "inherit",
      }}
    >
      <span style={{ fontSize: 13 }}>{data.label}</span>
      {data.secondary && (
        <span style={{ fontSize: 11, opacity: 0.7 }}>({data.secondary})</span>
      )}
    </div>
  );
};

const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select options...",
  avatarMode = false,
}) => {
  const normalizedValue = Array.isArray(value)
    ? value.map(String)
    : value !== undefined && value !== null && value !== ""
      ? [String(value)]
      : [];

  const formattedOptions = options.map((opt) => ({
    value: String(opt.value),
    label: opt.label,
    secondary: opt.secondary,
  }));

  const selectedValues = formattedOptions.filter((opt) =>
    normalizedValue.includes(String(opt.value)),
  );

  const handleChange = (selected) => {
    const values = selected ? selected.map((s) => s.value) : [];
    if (onChange) onChange(values);
  };

  return (
    <Select
      isMulti
      closeMenuOnSelect={false}
      options={formattedOptions}
      value={selectedValues}
      onChange={handleChange}
      placeholder={placeholder}
      classNamePrefix="react-select"
      menuPortalTarget={avatarMode ? document.body : undefined}
      menuPosition={avatarMode ? "fixed" : undefined}
      isSearchable
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
        ClearIndicator: () => null,
        IndicatorsContainer: () => null,
        ValueContainer: avatarMode
          ? AvatarValueContainer
          : CustomValueContainer,
        Option: CustomOption,
      }}
    />
  );
};

export default MultiSelect;
